import { Link } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import React, { useEffect, useState } from "react";
import { CircularProgress } from "@material-ui/core";
import { useDispatch, useSelector } from 'react-redux';

import { getDealsTable, getPipelines } from '../../store/Deals';
import TableAnimationFiles from '../../components/Animations/TableAnimationFiles.js';
import { formatProperties, getPropertyValue, formatPropertyValue } from '../../utils/propertyFormatter';

const TableView = ({ setSelectItems, activeView }) => {

  const dispatch = useDispatch();
  const { dealsTable: deals, pagination, loading } = useSelector(state => state['Deals']);
  const { properties } = useSelector(state => state['Objects']['deals']) || {};

  const [perPage, setPerPage] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [toggleCleared, setToggleCleared] = useState(false);
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);

  useEffect(() => { dispatch(getPipelines()) }, []);
  useEffect(() => { dispatch(getDealsTable({ page: currentPage, limit: perPage })) }, [currentPage, perPage]);

  // Pagination handlers
  const handlePageChange = (page) => setCurrentPage(page);

  const handleSelectedRowsChange = ({ selectedRows }) => setSelectItems(selectedRows.map(row => row['_id']));
  const handlePerRowsChange = (newPerPage, page) => {
    setPerPage(newPerPage);
    setCurrentPage(page);
  };

  const isPaginated = pagination.total > perPage;
  const headerScrollHeight = isPaginated ? 'calc(100vh - 189px)' : 'calc(100vh - 161px)';
  const responsiveClass = isPaginated ? 'datatable--paginated' : 'datatable--fullheight';

  // Get columns based on active view or use default columns
  const getColumns = () => {
    const baseColumns = {
      title: {
        id: 'title',
        name: 'Deal Name',
        width: '300px',
        selector: row => row['title'],
        sortable: true,
        cell: row => (
          <div className="custom-tooltip-wrapper">
            <Link
              to={`/deals/${row['_id']}`}
              title={row['title']}
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: 'inline-block',
                width: '100%',
                fontSize: '13px',
                color: 'var(--primary-color)',
                fontWeight: '600'
              }}
            >
              {row['title']}
            </Link>
          </div>
        ),
      },
      stage: {
        id: 'stage',
        name: 'Deal Stage',
        width: '200px',
        selector: row => row['stage'],
        sortable: true,
        cell: row => (<div title={row['stage']} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row['stage']}</div>),
      },
      closeDate: {
        id: 'closeDate',
        name: 'Close Date',
        width: '150px',
        selector: row => row['closeDate'],
        sortable: true,
        cell: row => {
          const date = row['closeDate'] ? new Date(row['closeDate']).toLocaleDateString() : '-';
          return (<div title={date} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{date}</div>);
        },
      },
      owner: {
        id: 'owner',
        name: 'Deal Owner',
        width: '250px',
        selector: row => row['ownerId'],
        sortable: true,
        cell: row => {
          const ownerName = row['ownerId'] ? `${row['ownerId']['firstName'] || ''} ${row['ownerId']['lastName'] || ''}`.trim() : '-';
          return (<div title={ownerName} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ownerName}</div>);
        },
      },
      pipeline: {
        id: 'pipeline',
        name: 'Pipeline',
        width: '170px',
        selector: row => row['pipelineId'],
        sortable: true,
        cell: row => {
          const pipelineName = row['pipelineId']?.['name'] || '-';
          return (<div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{pipelineName}</div>);
        },
      },
      amount: {
        id: 'amount',
        name: 'Amount',
        width: '150px',
        selector: row => row['amount'],
        sortable: true,
        cell: row => {
          const formattedAmount = row['amount'] ? `$${row['amount'].toLocaleString()}` : '-';
          return (<div title={formattedAmount} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{formattedAmount}</div>);
        },
      },
      tags: {
        id: 'tags',
        name: 'Tags',
        width: '200px',
        selector: row => row['tags'],
        sortable: false,
        cell: row => {
          if (!row['tags'] || row['tags'].length === 0) {
            return <span style={{ color: '#999' }}>No tags</span>;
          }

          return (
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              {row['tags'].filter(tag => tag != null).slice(0, 3).map((tag, index) => {
                const tagName = typeof tag === 'string' ? tag : (tag?.name || '');
                const tagColor = typeof tag === 'string' ? { bg: '#e0e0e0', color: '#666' } : { bg: tag?.color || '#e0e0e0', color: tag?.color ? 'white' : '#666' };
                if (!tagName) return null;
                return (
                  <span
                    key={`${tagName}-${index}`}
                    style={{
                      backgroundColor: tagColor.bg || tagColor,
                      color: tagColor.color || 'white',
                      padding: '2px 6px',
                      borderRadius: '3px',
                      fontSize: '11px',
                      display: 'inline-block'
                    }}
                  >
                    {tagName}
                  </span>
                );
              })}
              {row['tags'].filter(tag => tag != null).length > 3 && (
                <span style={{ color: '#666', fontSize: '11px' }}>
                  +{row['tags'].filter(tag => tag != null).length - 3}
                </span>
              )}
            </div>
          );
        },
      },
    };

    // If a view is selected, use its columns
    if (activeView && activeView['columns'] && activeView['columns'].length > 0) {
      const viewColumns = activeView['columns']
        .filter(col => col['isVisible'] !== false)
        .sort((a, b) => (a['order'] || 0) - (b['order'] || 0))
        .map(col => {
          const baseCol = baseColumns[col['propertyName']];
          if (baseCol) return { ...baseCol, width: col['width'] ? `${col['width']}px` : baseCol['width'] };
          // For custom properties not in baseColumns, create a generic column
          // Find the property definition to determine if it's a custom field
          const formattedProps = formatProperties(properties, 'deals');
          const property = formattedProps.find(p => p.name === col['propertyName']);
          
          return {
            id: col['propertyName'],
            name: col['displayName'] || col['propertyName'].charAt(0).toUpperCase() + col['propertyName'].slice(1),
            width: `${col['width'] || 200}px`,
            selector: row => getPropertyValue(row, property),
            sortable: true,
            cell: row => {
              const value = getPropertyValue(row, property);
              const formatted = property ? formatPropertyValue(value, property) : (value || '-');
              return (
                <div title={formatted} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {formatted}
                </div>
              );
            }
          };
        });
      
      // Always ensure the title column is first and locked
      const titleColumnExists = viewColumns.some(col => col['id'] === 'title');
      if (!titleColumnExists) {
        viewColumns.unshift(baseColumns['title']);
      } else {
        // Move title column to first position if it exists
        const titleColIndex = viewColumns.findIndex(col => col['id'] === 'title');
        if (titleColIndex > 0) {
          const titleCol = viewColumns.splice(titleColIndex, 1)[0];
          viewColumns.unshift(titleCol);
        }
      }
      
      return viewColumns;
    }

    // Default columns if no view is selected
    return Object.values(baseColumns);
  };

  const columns = getColumns();

  return (
    <div className="table-container filters-table deal-table">
      <style>{tableStyles}</style>
      <DataTable
        columns={columns}
        fixedHeader
        data={deals}
        progressPending={loading}
        progressComponent={<TableAnimationFiles />}
        pagination
        paginationServer
        paginationTotalRows={pagination.total || 0}
        onChangePage={handlePageChange}
        onChangeRowsPerPage={handlePerRowsChange}
        paginationDefaultPage={currentPage}
        className={`table-sellagent ${responsiveClass}`}
        fixedHeaderScrollHeight={headerScrollHeight}
        paginationPerPage={perPage}
        paginationRowsPerPageOptions={[10, 25, 50, 100]}
        paginationResetDefaultPage={resetPaginationToggle}
        paginationComponentOptions={{
          selectAllRowsItem: false,
          selectAllRowsItemText: 'All',
          rangeSeparatorText: 'of',
          rowsPerPageText: 'per page:',
        }}
        selectableRows
        persistTableHead
        noDataComponent={<div className='notfound-area'>
          <div className='img-box'>
            <img alt="..." src={require('../../static/img/not-found.png')} />
          </div>
          <p>No deal found</p>
        </div>}
        clearSelectedRows={toggleCleared}
        onSelectedRowsChange={handleSelectedRowsChange}
      />
    </div>
  );
}

// Dynamic columns now managed through ViewManager component

const tableStyles = `
          .table-sellagent input[type="checkbox"] {
            width: 13px;
          height: 13px;
          cursor: pointer;
          accent-color: #cbd6e2;
          transform: scale(1);
          opacity: 0.45;
  }

          .table-sellagent input[type="checkbox"]:checked {
            opacity: 1;
          accent-color: var(--primary-color);
  }

          .rdt_TableRow {
            transition: background-color 0.15s ease;
  }

          .rdt_TableRow:hover {
            background - color: #f5f5f5;
  }

          .table-container {
            position: relative;
  }
          `;

export default TableView;